import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Search, UserPlus, MessageCircle, Filter, MapPin } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { coachServiceFixed as coachService } from '@/services/coachServiceFixed';
import { toast } from 'sonner';

const CoachNetwork = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser, profile: internalProfile } = useInternalAuth();
  
  const user = supabaseUser || internalUser;
  const userProfile = profile || internalProfile;
  const currentUserId = profile?.id || internalUser?.id;
  const isInternalUser = !!internalUser && !supabaseUser;
  const isMobile = useIsMobile();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [connections, setConnections] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [certificationFilter, setCertificationFilter] = useState('');
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');

  // Redirect if not authenticated or not a coach
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (userProfile?.role !== 'coach') {
    return <Navigate to="/dashboard" replace />;
  }

  const loadConnections = async () => {
    if (!currentUserId) return;
    
    try {
      // Note: Coach connections functionality not yet implemented in coachServiceFixed
      const coachConnections: any[] = [];
      const userConnections = { connections: [] };
      
      // Combine both types of connections
      const allConnections = [
        ...coachConnections,
        ...(userConnections.connections || []).map((conn: any) => ({
          id: conn.id,
          coach_id: currentUserId,
          connected_coach_id: conn.connected_user_id || conn.user_id,
          connection_type: 'colleague',
          is_active: true,
          created_at: conn.created_at,
          coach_profile: {
            username: conn.username,
            first_name: conn.first_name,
            last_name: conn.last_name,
            role: conn.role
          }
        }))
      ];
      
      setConnections(allConnections);
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const searchCoaches = async () => {
    if (!searchTerm.trim() || !currentUserId) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      // Note: Search coaches functionality not yet implemented in coachServiceFixed
      const results: any[] = [];
      
      // Filter out already connected coaches
      const connectedIds = connections.map(c => c.connected_coach_id);
      let filtered = results.filter(coach => !connectedIds.includes(coach.user_id));
      
      // Apply additional filters
      if (certificationFilter.trim()) {
        filtered = filtered.filter(coach => 
          coach.coaching_certifications?.toLowerCase().includes(certificationFilter.toLowerCase())
        );
      }
      
      if (locationFilter.trim()) {
        filtered = filtered.filter(coach => 
          coach.location?.toLowerCase().includes(locationFilter.toLowerCase()) ||
          coach.city?.toLowerCase().includes(locationFilter.toLowerCase()) ||
          coach.state?.toLowerCase().includes(locationFilter.toLowerCase())
        );
      }
      
      if (experienceFilter !== 'all') {
        // This would require additional fields in the coach profile
        // For now, we'll keep it as a placeholder
      }
      
      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching coaches:', error);
      toast.error('Failed to search coaches');
    } finally {
      setLoading(false);
    }
  };

  const connectWithCoach = async (targetCoachId: string) => {
    if (!currentUserId) return;

    try {
      // Note: Connect with coach functionality not yet implemented in coachServiceFixed
      // await coachService.connectWithCoach(currentUserId, targetCoachId);
      toast.success('Connection request sent successfully');
      
      // Remove from search results and refresh connections
      setSearchResults(prev => prev.filter(coach => coach.user_id !== targetCoachId));
      loadConnections();
    } catch (error) {
      console.error('Error connecting with coach:', error);
      toast.error('Failed to connect with coach');
    }
  };

  const getDisplayName = () => {
    if (isInternalUser && internalUser) {
      return internalProfile?.first_name || internalUser.username;
    }
    return profile?.first_name || 
           supabaseUser?.user_metadata?.first_name || 
           supabaseUser?.user_metadata?.username || 
           'Coach';
  };

  useEffect(() => {
    loadConnections();
  }, [currentUserId]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchCoaches();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, connections, certificationFilter, locationFilter, experienceFilter]);

  return (
    <div className="flex flex-col min-h-screen bg-black">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className="flex-grow mt-32 py-6 md:py-12 px-4 md:px-6 pb-20 md:pb-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Coach Network
            </h1>
            <p className="text-gray-400">
              Connect with fellow coaches, share knowledge, and grow your network
            </p>
          </div>

          {/* Search Section */}
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardHeader>
              <CardTitle className="text-sports-green flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Search & Filter Coaches
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search coaches by name or username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-900 border-gray-700 text-white"
                />
              </div>
              
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Input
                    placeholder="Certifications"
                    value={certificationFilter}
                    onChange={(e) => setCertificationFilter(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
                
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Location (city, state)"
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="pl-10 bg-gray-900 border-gray-700 text-white"
                  />
                </div>
                
                <Select value={experienceFilter} onValueChange={setExperienceFilter}>
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Experience Level" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="beginner">Beginner (0-2 years)</SelectItem>
                    <SelectItem value="intermediate">Intermediate (3-5 years)</SelectItem>
                    <SelectItem value="experienced">Experienced (6-10 years)</SelectItem>
                    <SelectItem value="expert">Expert (10+ years)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          {/* Search Results Section */}
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardHeader>
              <CardTitle className="text-sports-green flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search Results
              </CardTitle>
            </CardHeader>
            <CardContent>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-white">Search Results</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {searchResults.map((coach) => (
                      <div key={coach.user_id} className="p-4 bg-gray-900/50 rounded-lg">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-white">
                              {coach.username || `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || 'Coach'}
                            </h4>
                            <Badge variant="outline" className="text-sports-green border-sports-green mt-1">
                              {coach.user_type === 'supabase' ? 'Supabase' : 'Internal'} Coach
                            </Badge>
                          </div>
                          <Button
                            onClick={() => connectWithCoach(coach.user_id)}
                            size="sm"
                            className="bg-sports-green text-black hover:bg-green-600"
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Connect
                          </Button>
                        </div>
                        
                        {coach.bio && (
                          <p className="text-sm text-gray-400 mb-2">{coach.bio}</p>
                        )}
                        
                        {coach.coaching_certifications && (
                          <p className="text-xs text-gray-500">
                            Certifications: {coach.coaching_certifications}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loading && (
                <div className="text-center py-4">
                  <p className="text-gray-400">Searching coaches...</p>
                </div>
              )}
              
              {!loading && searchTerm && searchResults.length === 0 && (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">No coaches found matching your criteria.</p>
                  <p className="text-sm text-gray-500">
                    Try adjusting your search terms or filters.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Connections */}
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardHeader>
              <CardTitle className="text-sports-green flex items-center gap-2">
                <Users className="h-5 w-5" />
                Your Connections ({connections.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {connections.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {connections.map((connection) => (
                    <div key={connection.id} className="p-4 bg-gray-900/50 rounded-lg">
                      <div className="flex items-start justify-between mb-3">
                       <div>
                         <h4 className="font-medium text-white">
                           {connection.coach_profile?.username || 
                            `${connection.coach_profile?.first_name || ''} ${connection.coach_profile?.last_name || ''}`.trim() ||
                            `Coach ${connection.connected_coach_id.substring(0, 8)}`}
                         </h4>
                         <Badge variant="outline" className="text-blue-400 border-blue-400 mt-1">
                           {connection.connection_type} • {connection.coach_profile?.role || 'Coach'}
                         </Badge>
                       </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-gray-600 text-gray-300 hover:bg-gray-600"
                        >
                          <MessageCircle className="h-4 w-4 mr-1" />
                          Message
                        </Button>
                      </div>
                      
                      <p className="text-xs text-gray-500">
                        Connected {new Date(connection.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">You haven't connected with any coaches yet.</p>
                  <p className="text-sm text-gray-500">
                    Use the search above to find and connect with other coaches.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default CoachNetwork;